import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Alert, AlertDescription } from '../../ui/alert';
import { Label } from '../../ui/label';
import { Loader2, AlertCircle, Download } from 'lucide-react';
import { useReportData } from '../../../hooks/useReportData';
import { useExcelExport } from '../../../hooks/useExcelExport';
import { userService } from '../../../_services';

/**
 * Commission Payout Report Dialog
 * Modernized modal for downloading commission payout reports
 * Downloads styled Excel with alternating row colors
 */
function CommissionPayoutDialog({ open, onOpenChange, title }) {
    const [selectedMonth, setSelectedMonth] = useState('');
    const [error, setError] = useState('');
    const [downloading, setDownloading] = useState(false);

    // Fetch month list only when dialog is open
    const { data: monthData, loading: loadingMonths } = useReportData('commissionPayoutMonths', {}, open);

    const { exportToStyledExcel } = useExcelExport();

    // Set default month when data loads
    useEffect(() => {
        if (monthData && monthData.length > 0 && !selectedMonth) {
            setSelectedMonth(monthData[0].date_Month);
        }
    }, [monthData, selectedMonth]);

    // Reset state when dialog closes
    useEffect(() => {
        if (!open) {
            setError('');
            setDownloading(false);
        }
    }, [open]);

    // Download report handler
    const handleDownload = async () => {
        if (!selectedMonth) {
            setError('Please select a month');
            return;
        }

        setDownloading(true);
        setError('');

        try {
            const usermetaData = window.atob(localStorage.getItem('ajs_user_id'));
            const userId = usermetaData.split(':')[0];
            const userName = usermetaData.split(':')[1];
            const userRole = window.atob(localStorage.getItem('UserRole'));

            const response = await userService.FetchPayoutReportLogs(userId, userName, userRole);
            
            if (!response || response.length === 0) {
                setError('No data available for the selected month');
                setDownloading(false);
                return;
            }

            // Transform data for Excel with proper column names
            const excelData = response.map(item => ({
                "RecipientCompanyAssignedUniqueKey(primary group)": item.primary_Group || '',
                "Additionalgroups": item.additionalgroups || '',
                "FirstName": item.firstName || '',
                "LastName": item.lastName || '',
                "RecipientNotificationEmailAddress": item.emailAddress || '',
                "Earned Commission": item.earned_Commission || 0,
                "Commission for Sub Distributor": item.commission_for_SubDist || 0,
                "Commission Due to Distributor": item.commission_Due_to_Distributor || 0,
                "Actual Earned Commission": item.actual_Earned || 0,
                "Avalara W9 Tax Status": item.avalaraW9Tax_Status || '',
                "Wise Account Status": item.wiseAccount_Status || '',
                "PQ Invite Status": item.pqInvite_Status || '',
                "PQ Registration Status": item.pqRegistration_Status || ''
            }));

            // Generate filename from month
            const monthFormatted = selectedMonth.split('T')[0].trim().split(',').join('_');
            const filename = `NuLifeSpanRx_Commission_Payout_Report_${monthFormatted}`;

            // Export with styling (alternating row colors, styled header)
            await exportToStyledExcel(excelData, filename, {
                headerColor: '4F81BD',
                oddRowColor: 'DBE5F1',
                evenRowColor: 'B8CCE4',
                sheetName: 'Commission Payout Report'
            });

            // Close dialog on success
            setTimeout(() => {
                onOpenChange(false);
            }, 500);

        } catch (err) {
            console.error('Error downloading commission payout report:', err);
            setError('Failed to download report. Please try again.');
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle className="text-xl font-semibold text-brand-dark">
                        {title} Report
                    </DialogTitle>
                    <DialogDescription>
                        Select a month to download the commission payout report as Excel
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {/* Month Selector */}
                    <div className="space-y-2">
                        <Label htmlFor="month-select">Choose Date</Label>
                        {loadingMonths ? (
                            <div className="flex items-center gap-2 text-brand-dark/70">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                <span>Loading months...</span>
                            </div>
                        ) : monthData && monthData.length > 0 ? (
                            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                                <SelectTrigger id="month-select" className="w-full">
                                    <SelectValue placeholder="Select Month" />
                                </SelectTrigger>
                                <SelectContent>
                                    {monthData.map((item, index) => (
                                        <SelectItem key={index} value={item.date_Month}>
                                            {item.date_Month}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        ) : (
                            <Alert variant="destructive">
                                <AlertCircle className="h-4 w-4" />
                                <AlertDescription>
                                    No months available. Please try again later.
                                </AlertDescription>
                            </Alert>
                        )}
                    </div>

                    {/* Error Message */}
                    {error && (
                        <Alert variant="destructive">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={downloading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDownload}
                        disabled={downloading || loadingMonths || !selectedMonth}
                        variant="secondary"
                    >
                        {downloading ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Downloading...
                            </>
                        ) : (
                            <>
                                <Download className="mr-2 h-4 w-4" />
                                Download
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default CommissionPayoutDialog;
